import React from "react";
import { motion, useInView, type Variants } from "framer-motion";

export const wdViewport = { once: false, amount: 0.2 } as const;
export const wdViewportEarly = { once: false, amount: 0.1 } as const;

export const fadeUp: Variants = {
    hidden: { opacity: 0, y: 24 },
    visible: (delay = 0) => ({
        opacity: 1,
        y: 0,
        transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1], delay },
    }),
};

export const slideFromLeft: Variants = {
    hidden: { opacity: 0, x: -48 },
    visible: {
        opacity: 1,
        x: 0,
        transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
    },
};

export const fadeScale: Variants = {
    hidden: { opacity: 0, scale: 0.94 },
    visible: (delay = 0) => ({
        opacity: 1,
        scale: 1,
        transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1], delay },
    }),
};

export const fadeUpFromBottom: Variants = {
    hidden: { opacity: 0, y: 36 },
    visible: (delay = 0) => ({
        opacity: 1,
        y: 0,
        transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1], delay },
    }),
};

export const staggerContainer: Variants = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.12, delayChildren: 0.06 } },
};

export const portfolioStagger: Variants = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.09, delayChildren: 0.05 } },
};

export const portfolioGridReveal: Variants = {
    hidden: {
        opacity: 0,
        clipPath: "inset(100% 6% 0 6%)",
        filter: "blur(4px)",
    },
    visible: (delay = 0) => ({
        opacity: 1,
        clipPath: "inset(0% 0% 0% 0%)",
        filter: "blur(0px)",
        transition: { duration: 0.72, ease: [0.22, 1, 0.36, 1], delay },
    }),
};

export const WdReveal = ({
    children,
    className,
    variants = fadeUp,
    delay = 0,
}: {
    children: React.ReactNode;
    className?: string;
    variants?: Variants;
    delay?: number;
}) => (
    <motion.div
        className={className}
        initial="hidden"
        whileInView="visible"
        viewport={wdViewport}
        variants={variants}
        custom={delay}
    >
        {children}
    </motion.div>
);

export const useWdInView = (
    options: {
        margin?: `${number}px` | `${number}%`;
        amount?: number;
    } = {},
) => {
    const ref = React.useRef<HTMLElement | null>(null);
    const inView = useInView(ref, {
        once: false,
        amount: options.amount ?? 0.2,
        margin: options.margin ?? "0px",
    });
    return { ref, inView };
};
